import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { MapPin, Upload, X, Image, Video, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://silver-chicken-493994.hostingersite.com';
const MAPBOX_TOKEN = window.REACT_APP_MAPBOX_TOKEN;

const NewProperty = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const mapContainer = useRef(null);
  const map = useRef(null);
  
  // File states
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadedVideos, setUploadedVideos] = useState([]);
  
  const [viewState, setViewState] = useState({
    longitude: 78.4867,
    latitude: 10.7905,
    zoom: 7,
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: 'flat',
    selling_price: '',
    commission_percent: 1.0,
    address: '',
    district: '',
    city: '',
    village: '',
    state: 'Tamil Nadu',
    pincode: '',
    latitude: '',
    longitude: '',
    land_area: '',
    built_up_area: '',
    bedrooms: 0,
    bathrooms: 0,
    floor_number: '',
    total_floors: '',
    facing: '',
    north_neighbor: '',
    south_neighbor: '',
    east_neighbor: '',
    west_neighbor: '',
    survey_number: '',
    patta_number: '',
    chitta_number: '',
  });

  // Document types for Tamil Nadu property
  const documentTypes = [
    { id: 'patta', label: 'Patta', required: true },
    { id: 'chitta', label: 'Chitta', required: true },
    { id: 'fmb', label: 'FMB (Field Measurement Book)', required: true },
    { id: 'ec', label: 'Encumbrance Certificate', required: true },
    { id: 'sale_deed', label: 'Sale Deed / Registration', required: true },
    { id: 'survey_map', label: 'Survey Map', required: false },
    { id: 'tax_receipt', label: 'Property Tax Receipt', required: false },
    { id: 'approval_plan', label: 'Building Approval Plan', required: false },
  ];

  // Initialize map
  useEffect(() => {
    if (!MAPBOX_TOKEN || !mapContainer.current || map.current) return;
    if (typeof window.mapboxgl === 'undefined') return;

    window.mapboxgl.accessToken = MAPBOX_TOKEN;

    map.current = new window.mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [viewState.longitude, viewState.latitude],
      zoom: viewState.zoom,
    });

    map.current.addControl(new window.mapboxgl.NavigationControl(), 'top-left');

    // Click handler for pinning
    map.current.on('click', (e) => {
      const { lng, lat } = e.lngLat;
      setSelectedLocation({ latitude: lat, longitude: lng });
      setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
      
      // Add marker
      if (map.current) {
        const markerEl = document.createElement('div');
        markerEl.className = 'w-8 h-8 bg-red-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center animate-pulse';
        markerEl.innerHTML = '<div class="w-2 h-2 bg-white rounded-full"></div>';
        
        new window.mapboxgl.Marker(markerEl)
          .setLngLat([lng, lat])
          .addTo(map.current);
      }
      
      toast.success('📍 Location pinned on map!');
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Fetch districts
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/districts`);
        setDistricts(response.data || []);
      } catch (error) {
        console.error('Error fetching districts:', error);
      }
    };
    fetchDistricts();
  }, []);

  // Check if user is mediator
  useEffect(() => {
    if (user && user.role !== 'mediator' && user.role !== 'admin') {
      toast.error('Only mediators can post properties');
      navigate('/');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Image upload handler
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 10) {
      toast.error('Maximum 10 images allowed');
      return;
    }
    setImages(files);
    const previews = files.map(file => URL.createObjectURL(file));
    setUploadedImages(previews);
  };

  // Video upload handler
  const handleVideoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 3) {
      toast.error('Maximum 3 videos allowed');
      return;
    }
    setVideos(files);
    const previews = files.map(file => URL.createObjectURL(file));
    setUploadedVideos(previews);
  };

  // Document upload handler
  const handleDocumentUpload = (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setDocuments(prev => [...prev, { type: docType, file, name: file.name }]);
    toast.success(`📄 ${docType} uploaded successfully`);
  };

  // Remove document
  const removeDocument = (index) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  // Upload to Cloudinary
  const uploadToCloudinary = async (file, folder) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'nalla_brokers');
    formData.append('folder', `nalla-brokers/${folder}`);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/i06itgyh/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Cloudinary error:', errorData);
        throw new Error(errorData.error?.message || 'Upload failed');
      }
      
      const data = await response.json();
      return data.secure_url;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.selling_price) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.latitude || !formData.longitude) {
      toast.error('Please click on the map to pin the property location');
      return;
    }

    if (!token) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }

    setLoading(true);
    setUploadingFiles(true);

    try {
      // Upload images
      const uploadedImageUrls = [];
      for (const image of images) {
        const url = await uploadToCloudinary(image, 'images');
        uploadedImageUrls.push(url);
      }

      // Upload videos
      const uploadedVideoUrls = [];
      for (const video of videos) {
        const url = await uploadToCloudinary(video, 'videos');
        uploadedVideoUrls.push(url);
      }

      // Upload documents
      const uploadedDocUrls = [];
      for (const doc of documents) {
        const url = await uploadToCloudinary(doc.file, 'documents');
        uploadedDocUrls.push({ type: doc.type, url, name: doc.name });
      }

      setUploadingFiles(false);

      // Create property
      const propertyData = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        selling_price: parseFloat(formData.selling_price),
        land_area: formData.land_area ? parseFloat(formData.land_area) : null,
        built_up_area: formData.built_up_area ? parseFloat(formData.built_up_area) : null,
        bedrooms: parseInt(formData.bedrooms) || 0,
        bathrooms: parseInt(formData.bathrooms) || 0,
        images: uploadedImageUrls,
        videos: uploadedVideoUrls,
        documents: uploadedDocUrls,
      };

      const response = await axios.post(
        `${API_URL}/api/properties`,
        propertyData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      toast.success('✅ Property posted successfully!');
      navigate('/mediator-dashboard');
      
    } catch (error) {
      console.error('Error posting property:', error);
      toast.error(error.response?.data?.error || 'Failed to post property');
      setUploadingFiles(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">📝 Post New Property</h1>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* LEFT: Property Details */}
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Property Title *
              </label>
              <input
                type="text"
                name="title"
                required
                className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                placeholder="e.g., Luxury 3BHK Apartment in Anna Nagar"
                value={formData.title}
                onChange={handleChange}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Description *
              </label>
              <textarea
                name="description"
                required
                rows="4"
                className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                placeholder="Describe the property in detail..."
                value={formData.description}
                onChange={handleChange}
              />
            </div>

            {/* Property Type & Price */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Property Type *
                </label>
                <select
                  name="property_type"
                  required
                  className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  value={formData.property_type}
                  onChange={handleChange}
                >
                  <option value="flat">Flat</option>
                  <option value="house">House</option>
                  <option value="villa">Villa</option>
                  <option value="plot">Plot</option>
                  <option value="land">Land</option>
                  <option value="commercial">Commercial</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Selling Price (₹) *
                </label>
                <input
                  type="number"
                  name="selling_price"
                  required
                  className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="e.g., 8500000"
                  value={formData.selling_price}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Location Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  District *
                </label>
                <select
                  name="district"
                  required
                  className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  value={formData.district}
                  onChange={handleChange}
                >
                  <option value="">Select District</option>
                  {districts.map((d) => (
                    <option key={d.district_code} value={d.district_name}>
                      {d.district_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  required
                  className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="e.g., Chennai"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Village
                </label>
                <input
                  type="text"
                  name="village"
                  className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="e.g., Annanagar"
                  value={formData.village}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Address *
                </label>
                <input
                  type="text"
                  name="address"
                  required
                  className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="Street, Area, Landmark..."
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Survey Numbers */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Survey No.
                </label>
                <input
                  type="text"
                  name="survey_number"
                  className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="Survey #"
                  value={formData.survey_number}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Patta No.
                </label>
                <input
                  type="text"
                  name="patta_number"
                  className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="Patta #"
                  value={formData.patta_number}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Chitta No.
                </label>
                <input
                  type="text"
                  name="chitta_number"
                  className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="Chitta #"
                  value={formData.chitta_number}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Specifications */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Land Area (sq.ft)
                </label>
                <input
                  type="number"
                  name="land_area"
                  className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="e.g., 1200"
                  value={formData.land_area}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Bedrooms
                </label>
                <input
                  type="number"
                  name="bedrooms"
                  className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="e.g., 3"
                  value={formData.bedrooms}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Bathrooms
                </label>
                <input
                  type="number"
                  name="bathrooms"
                  className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="e.g., 2"
                  value={formData.bathrooms}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Facing
                </label>
                <select
                  name="facing"
                  className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  value={formData.facing}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="North">North</option>
                  <option value="South">South</option>
                  <option value="East">East</option>
                  <option value="West">West</option>
                  <option value="North-East">North-East</option>
                  <option value="North-West">North-West</option>
                  <option value="South-East">South-East</option>
                  <option value="South-West">South-West</option>
                </select>
              </div>
            </div>

            {/* 4-Side Neighbor Details */}
            <div className="border border-neutral-200 p-4 rounded-xl bg-neutral-50">
              <h4 className="font-semibold text-neutral-700 mb-3">🏘️ Boundary Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-neutral-600 mb-1">North</label>
                  <input
                    type="text"
                    name="north_neighbor"
                    className="w-full p-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Neighbor name"
                    value={formData.north_neighbor}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-600 mb-1">South</label>
                  <input
                    type="text"
                    name="south_neighbor"
                    className="w-full p-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Neighbor name"
                    value={formData.south_neighbor}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-600 mb-1">East</label>
                  <input
                    type="text"
                    name="east_neighbor"
                    className="w-full p-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Neighbor name"
                    value={formData.east_neighbor}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-600 mb-1">West</label>
                  <input
                    type="text"
                    name="west_neighbor"
                    className="w-full p-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Neighbor name"
                    value={formData.west_neighbor}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Map + Uploads */}
          <div className="space-y-6">
            {/* Map for Pinning */}
            <div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-blue-700 font-medium">
                  📍 <strong>Click on the map below</strong> to pin the exact property location
                </p>
              </div>
              
              <div className="h-[400px] border-2 border-dashed border-blue-300 rounded-xl overflow-hidden relative bg-neutral-50">
                <div ref={mapContainer} className="w-full h-full" />
                
                {!selectedLocation && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-white/90 px-6 py-3 rounded-lg shadow-lg text-neutral-600 text-sm">
                      👆 Click anywhere on the map to pin location
                    </div>
                  </div>
                )}
              </div>
              
              {selectedLocation ? (
                <div className="mt-3 text-sm text-green-700 bg-green-50 p-3 rounded-lg border border-green-200">
                  ✅ <strong>Location pinned!</strong><br />
                  {selectedLocation.latitude.toFixed(6)}, {selectedLocation.longitude.toFixed(6)}
                </div>
              ) : (
                <div className="mt-3 text-sm text-orange-700 bg-orange-50 p-3 rounded-lg border border-orange-200">
                  ⚠️ <strong>Required:</strong> Click on the map to pin the property location
                </div>
              )}
            </div>

            {/* IMAGE UPLOAD */}
            <div className="border border-neutral-200 rounded-xl p-4">
              <h4 className="font-semibold text-neutral-700 mb-2 flex items-center gap-2">
                <Image className="w-5 h-5 text-primary-500" />
                Property Photos (Max 10)
              </h4>
              <input
                type="file"
                multiple
                accept="image/*"
                className="w-full p-2 border border-neutral-200 rounded-lg"
                onChange={handleImageUpload}
              />
              {uploadedImages.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {uploadedImages.map((url, index) => (
                    <div key={index} className="relative w-16 h-16 rounded-lg overflow-hidden border border-neutral-200">
                      <img src={url} alt={`Preview ${index}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                  <span className="text-xs text-neutral-500 mt-1 block">
                    {uploadedImages.length} images selected
                  </span>
                </div>
              )}
            </div>

            {/* VIDEO UPLOAD */}
            <div className="border border-neutral-200 rounded-xl p-4">
              <h4 className="font-semibold text-neutral-700 mb-2 flex items-center gap-2">
                <Video className="w-5 h-5 text-primary-500" />
                Property Videos (Max 3)
              </h4>
              <input
                type="file"
                multiple
                accept="video/*"
                className="w-full p-2 border border-neutral-200 rounded-lg"
                onChange={handleVideoUpload}
              />
              {uploadedVideos.length > 0 && (
                <span className="text-xs text-neutral-500 mt-1 block">
                  {uploadedVideos.length} videos selected
                </span>
              )}
            </div>

            {/* DOCUMENT UPLOAD */}
            <div className="border border-neutral-200 rounded-xl p-4">
              <h4 className="font-semibold text-neutral-700 mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-500" />
                Property Documents
              </h4>
              <p className="text-xs text-neutral-500 mb-3">
                Upload Patta, Chitta, FMB, EC, Sale Deed, etc.
              </p>
              <div className="space-y-2">
                {documentTypes.map((doc) => (
                  <div key={doc.id} className="flex items-center gap-2">
                    <label className="flex-1 text-sm text-neutral-600">
                      {doc.label} {doc.required && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.png"
                      className="text-sm"
                      onChange={(e) => handleDocumentUpload(e, doc.id)}
                    />
                  </div>
                ))}
              </div>
              {documents.length > 0 && (
                <div className="mt-2 space-y-1">
                  {documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between bg-neutral-50 p-2 rounded-lg">
                      <span className="text-sm text-neutral-600">{doc.name}</span>
                      <button
                        type="button"
                        onClick={() => removeDocument(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Commission */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Commission (%)
              </label>
              <input
                type="number"
                name="commission_percent"
                step="0.1"
                className="w-full p-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                placeholder="e.g., 1.0"
                value={formData.commission_percent}
                onChange={handleChange}
              />
              <p className="text-xs text-neutral-500 mt-1">Default: 1% from both buyer and seller</p>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex gap-4 pt-4 border-t border-neutral-200">
          <button
            type="button"
            onClick={() => navigate('/mediator-dashboard')}
            className="px-6 py-3 border border-neutral-200 rounded-xl hover:bg-neutral-50 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || uploadingFiles}
            className="px-6 py-3 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-all disabled:opacity-50 flex-1"
          >
            {loading || uploadingFiles ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                {uploadingFiles ? 'Uploading files...' : 'Posting...'}
              </span>
            ) : (
              'Post Property'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewProperty;